/**
 * Recover from "stale chunk after redeploy" failures.
 *
 * Vite emits hashed chunk names (e.g. ``AdminProduction-BpKX8Kaj.js``)
 * and inlines them into ``index.html``. After a redeploy the new HTML
 * references new hashes; the old hashes are gone from ``dist/``. A
 * tab still holding the pre-deploy ``index.html`` will 404 the moment
 * React tries to lazy-load a new route — exactly the
 * ``Failed to fetch dynamically imported module`` crash users see.
 *
 * The fix is a hard reload: the browser fetches the fresh
 * ``index.html`` (Vercel serves it with ``must-revalidate``), reads
 * the current hashes, and the lazy import succeeds. Two things make
 * this safe:
 *
 *  - We only reload ONCE per chunk-fail burst. A timestamp in
 *    ``sessionStorage`` (per-tab) records when the previous reload
 *    happened. If another chunk error fires within ``RELOAD_COOLDOWN_MS``
 *    we let it propagate to the normal error UI — that would loop on a
 *    genuinely missing asset. Outside the cooldown a fresh reload is
 *    allowed (handles the multi-deploy case where the user opens an
 *    old tab, recovers via reload, then sits on the new HTML across
 *    a SECOND deploy minutes later).
 *
 *  - We listen on four surfaces so chunk failures from any path
 *    converge on the same recovery:
 *      1. ``vite:preloadError`` — Vite's own event, fires on the
 *         dynamic-import path before the rejection bubbles further.
 *      2. ``components/error/RouteErrorPage`` (route ``errorElement``)
 *         — the one that catches a lazy ROUTE chunk. React Router owns
 *         a boundary inside ``<RouterProvider>`` and gets there first,
 *         so surfaces 3 and 4 below never see those.
 *      3. ``unhandledrejection`` — catches lazy-import promise
 *         rejections that escape Vite's own handler (older Vite,
 *         dev-mode HMR overlay disabled, certain Safari versions).
 *         Note React.lazy handles its own promise, so route chunks
 *         do not reach here.
 *      4. ``ErrorBoundary.componentDidCatch`` (in components/ErrorBoundary)
 *         — last-ditch render-time catch above the router.
 */

const RELOAD_FLAG = "forgmatic_chunk_reload_at";
// 5 minutes — long enough to cover the slowest "first paint → click →
// chunk fail" loop where preloadError might fire twice on the same
// HTML, but short enough that a same-tab user across two genuine
// redeploys (rare but possible during incidents) gets a second
// recovery attempt instead of a permanent fallback screen.
const RELOAD_COOLDOWN_MS = 5 * 60 * 1000;

function recentReloadAt(): number | null {
  if (typeof sessionStorage === "undefined") return null;
  try {
    const raw = sessionStorage.getItem(RELOAD_FLAG);
    if (!raw) return null;
    const ts = Number.parseInt(raw, 10);
    return Number.isFinite(ts) ? ts : null;
  } catch {
    return null;
  }
}

/** Did the previous attempt happen within the cooldown window? */
function inCooldown(): boolean {
  const ts = recentReloadAt();
  if (ts === null) return false;
  return Date.now() - ts < RELOAD_COOLDOWN_MS;
}

function setReloadFlag(): void {
  if (typeof sessionStorage === "undefined") return;
  try {
    sessionStorage.setItem(RELOAD_FLAG, String(Date.now()));
  } catch {
    /* private mode / quota — best effort */
  }
}

/**
 * A reload is scheduled but the page hasn't torn down yet.
 *
 * ``location.reload()`` doesn't stop execution — the tab keeps running
 * until the navigation commits. One navigation typically fires several
 * dynamic imports, so chunk failures arrive in a burst: without this
 * flag the first failure would set the cooldown timestamp and every
 * sibling failure microseconds later would read it back as "we already
 * tried, give up" and surface an error screen over a page that is in
 * fact already reloading.
 *
 * Module-scoped rather than in ``sessionStorage`` on purpose: it must
 * NOT survive the reload it describes.
 */
let reloadInFlight = false;

/** Is a chunk-recovery reload already scheduled for this page? */
export function isChunkReloadPending(): boolean {
  return reloadInFlight;
}

/** Hard-reload to pick up the post-deploy ``index.html``.
 *
 * Returns ``false`` only when we already reloaded inside the cooldown
 * window (so the caller lets the error propagate to the normal error
 * UI instead of looping). Returns ``true`` when a reload is scheduled
 * — either by this call or by an earlier failure in the same burst.
 */
export function attemptChunkReload(): boolean {
  // Already reloading — swallow this sibling failure rather than
  // letting the cooldown we just set refuse it into an error screen.
  if (reloadInFlight) return true;
  if (inCooldown()) return false;
  reloadInFlight = true;
  setReloadFlag();
  // Vercel serves index.html with ``must-revalidate`` so a regular
  // reload picks up the new hashes; ``location.reload()`` keeps it
  // simple and avoids confusing the BFCache.
  if (typeof window !== "undefined" && window.location?.reload) {
    window.location.reload();
  }
  return true;
}

/** Heuristic: does this error look like a stale-chunk failure? */
export function isChunkLoadError(error: unknown): boolean {
  if (!error) return false;
  const msg =
    typeof error === "string"
      ? error
      : error instanceof Error
        ? `${error.name}: ${error.message}`
        : "";
  // Chrome / Edge: ``ChunkLoadError: Loading chunk N failed.``
  // Firefox / Safari: ``TypeError: Failed to fetch dynamically imported module: …``
  // Vite preload (any browser): ``Failed to fetch dynamically imported module``
  // Webpack legacy: ``Loading CSS chunk … failed.``
  return (
    /failed to fetch dynamically imported module/i.test(msg) ||
    /loading chunk \S+ failed/i.test(msg) ||
    /loading css chunk \S+ failed/i.test(msg) ||
    /\bChunkLoadError\b/.test(msg)
  );
}

/** Install window-level listeners for chunk-load failures.
 *
 * Three sources converge on ``attemptChunkReload``:
 *
 * 1. ``vite:preloadError`` — Vite's own event, fires on the dynamic-
 *    import polyfill path. Calling ``preventDefault()`` tells Vite not
 *    to rethrow into the page (which would double-fire the recovery
 *    via the ErrorBoundary catch).
 * 2. ``unhandledrejection`` — catches lazy-import promise rejections
 *    that escape Vite's own handler. Real-world reports from
 *    ``forgmatic.com`` (apex domain, where /sign-up-team users see
 *    "Failed to fetch dynamically imported module" without the Vite
 *    listener firing) point at this path; older Vite versions and
 *    certain Safari/Firefox configurations surface the rejection
 *    here instead of as ``vite:preloadError``.
 * 3. ``ErrorBoundary.componentDidCatch`` (separate file) — the
 *    render-time catch for rejections that escape both events.
 *
 * Returns a cleanup function that removes the listeners — production
 * code never calls it (the listeners live for the lifetime of the
 * tab) but tests use it to keep handlers from leaking between
 * ``vi.resetModules()`` cycles.
 */
export function installChunkReloadListener(): () => void {
  if (typeof window === "undefined") return () => {};

  const preloadHandler = (event: Event) => {
    if (attemptChunkReload()) {
      event.preventDefault();
    }
  };

  const rejectionHandler = (event: PromiseRejectionEvent) => {
    if (!isChunkLoadError(event.reason)) return;
    if (attemptChunkReload()) {
      // Stop the rejection from also surfacing in the console /
      // Sentry — a stale-chunk reload is expected behaviour, not a
      // user-visible error.
      event.preventDefault();
    }
  };

  window.addEventListener("vite:preloadError", preloadHandler);
  window.addEventListener("unhandledrejection", rejectionHandler);
  return () => {
    window.removeEventListener("vite:preloadError", preloadHandler);
    window.removeEventListener("unhandledrejection", rejectionHandler);
  };
}
